
// Usage examples
// <video-with-toggle>
//   <button
//     data-pause-text="{{ 'sections.media_with_text.pause' | t }}"
//     data-play-text="{{ 'sections.media_with_text.play' | t }}"
//   >
//     {{ 'sections.media_with_text.pause' | t }}
//   </button>
//   {{ media | video_tag: class: 'js-video', autoplay: true, loop: true, controls: false, muted: true }}
// </video-with-toggle>
class VideoWithToggle extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');

    //? play
    this.playButton = this.querySelector('button[data-play-button]');
    if (this.playButton) {
      this.pauseText = this.playButton.dataset.pauseText;
      this.playText = this.playButton.dataset.playText;
    }

    this.pauseIcon = this.querySelector('span[data-pause-icon]');
    this.playIcon = this.querySelector('span[data-play-icon]');
    this.currentPlayIcon = this.querySelector('span[data-current-play-icon]');

    //? mute
    this.soundButton = this.querySelector('button[data-sound-button]');
    if (this.soundButton) {
      this.soundText = this.soundButton.dataset.soundText;
      this.muteText = this.soundButton.dataset.muteText;
    }
    this.soundIcon = this.querySelector('span[data-sound-icon]');
    this.muteIcon = this.querySelector('span[data-mute-icon]');
    this.currentSoundIcon = this.querySelector('span[data-current-sound-icon]');

    // Intersection Observer to pause video when out of view
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only pause if video is unmuted and playing
          if (!entry.isIntersecting && this.video && !this.video.paused && !this.video.muted) {
            this.pauseVideo();
          }
        });
      },
      { rootMargin: '150px' }
    );

    // Lazy videos (data-lazy, no autoplay) start playing once they near the viewport
    if (this.video?.hasAttribute('data-lazy')) {
      this.lazyObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.lazyObserver.disconnect();
          this.video.play();
        },
        { rootMargin: '200px' }
      );
    }
  }

  connectedCallback() {
    window.addEventListener('load', () => {
      if (!this.video) return;

      if (this.lazyObserver) this.lazyObserver.observe(this.video);
      if (this.playButton) this.playButton.addEventListener('click', (event) => this.toggleVideo(event));
      if (this.soundButton) this.soundButton.addEventListener('click', (event) => this.toggleSound(event));

      setTimeout(() => {
        this.observer.observe(this.video);
      }, 500);

      // Pause video when tab is hidden
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && !this.video.paused) {
          this.pauseVideo();
        }
      });
    });
  }

  disconnectedCallback() {
    this.observer.disconnect();
    if (this.lazyObserver) this.lazyObserver.disconnect();
    document.removeEventListener('visibilitychange');
  }

  playVideo() {
    this.video.play();
    this.currentPlayIcon.innerHTML = this.pauseIcon.innerHTML;
    if (this.playButton && this.pauseText) this.playButton.setAttribute('aria-label', this.pauseText);
  }

  pauseVideo() {
    this.video.pause();
    this.currentPlayIcon.innerHTML = this.playIcon.innerHTML;
    if (this.playButton && this.playText) this.playButton.setAttribute('aria-label', this.playText);
  }

  toggleVideo(event) {
    event.preventDefault();
    if (this.video.paused) {
      this.video.dataset.userPaused = 'false';
      this.playVideo();
    } else {
      this.video.dataset.userPaused = 'true';
      this.pauseVideo();
    }
  }

  toggleSound(event) {
    event.preventDefault();
    if (this.video.muted) {
      this.video.muted = false;
      this.currentSoundIcon.innerHTML = this.soundIcon.innerHTML;
      if (this.soundButton && this.muteText) this.soundButton.setAttribute('aria-label', this.muteText);
    } else {
      this.video.muted = true;
      this.currentSoundIcon.innerHTML = this.muteIcon.innerHTML;
      if (this.soundButton && this.soundText) this.soundButton.setAttribute('aria-label', this.soundText);
    }
  }
}

customElements.define('video-with-toggle', VideoWithToggle);
